/**
 * Which field guides hide behind the coach panel (WorldLayer): a guide is behind it only when the panel's opaque
 * parts really cover its drawn box — not when it merely stands near the panel's bounding box (QA round 1: lesson 1's
 * first cone, in plain view above the panel, had its mark hidden and its "HOP HERE ▼" label lifted into the sky).
 */

export interface Box {
  l: number;
  r: number;
  t: number;
  b: number;
  /** A panel part with the coach's line in it: a guide must never draw over it. */
  text?: boolean;
}

/**
 * A guide counts as behind the panel when this share of its drawn box is covered by the panel's tab / key strip,
 * or this (smaller) share by the panel's body (guides draw above the panel: never over its line).
 */
export const UNDER_SHARE = 0.3;
export const UNDER_TEXT_SHARE = 0.08;

/**
 * Is a guide's drawn box (`g`) behind the panel parts (`rects`)? Returns the top edge its label must clear, or null
 * when the guide is in plain view. Only real overlap counts (a cone whose shadow touches the tab is in view).
 */
export const coveredBy = (g: Box, rects: readonly Box[]): number | null => {
  const area = Math.max(1, (g.r - g.l) * (g.b - g.t));
  let covered = 0;
  let text = 0;
  let top = Infinity;
  for (const p of rects) {
    const w = Math.min(g.r, p.r) - Math.max(g.l, p.l);
    if (w <= 0) continue;
    // the label must clear every panel part in its column (lifted off the body, it must not land on the tab)
    top = Math.min(top, p.t);
    const h = Math.min(g.b, p.b) - Math.max(g.t, p.t);
    if (h <= 0) continue;
    covered += w * h;
    if (p.text) text += w * h;
  }
  return covered / area >= UNDER_SHARE || text / area >= UNDER_TEXT_SHARE ? top : null;
};
